import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import FleetEnquiryForm from "@/components/feature/FleetEnquiryForm";

const fleetChallenges = [
  { icon: "ri-money-dollar-circle-line", title: "Fuel & Running Cost", text: "Fuel and maintenance costs affect operating margins across a running fleet." },
  { icon: "ri-refresh-line", title: "Full Replacement Risk", text: "Replacing an entire fleet at once is a large capital commitment and disrupts operations." },
  { icon: "ri-time-line", title: "Operational Disruption", text: "Vehicles out of service during transition can affect service commitments." },
  { icon: "ri-map-pin-line", title: "Charging Uncertainty", text: "Charging planning has to match routes, depot space and vehicle duty cycles." },
];

const fleetSteps = [
  { icon: "ri-search-eye-line", title: "Vehicle Assessment", text: "Each candidate vehicle is reviewed for condition, architecture and retrofit suitability." },
  { icon: "ri-filter-3-line", title: "Prioritisation", text: "Vehicles best suited to retrofit are identified and sequenced." },
  { icon: "ri-calendar-check-line", title: "Fleet Planning", text: "A phased plan aligns retrofit work with your operating requirements." },
  { icon: "ri-charging-pile-line", title: "Charging Planning", text: "Charging requirements are planned alongside the vehicles and their routes." },
  { icon: "ri-rocket-line", title: "Deployment", text: "Retrofitted vehicles move into service in phases rather than all at once." },
  { icon: "ri-radar-line", title: "Monitoring", text: "Connected concepts aim to give visibility over vehicles and charging." },
  { icon: "ri-tools-line", title: "Maintenance", text: "Service and maintenance expectations are confirmed after assessment." },
];

export default function Fleet() {
  return (
    <>
      <Seo
        title="Fleet Electrification | EV Retrofit for Fleets"
        description="Electrify your fleet without replacing everything. Fleet assessment, retrofit planning, charging planning, deployment, monitoring and maintenance for commercial fleets."
        keywords="fleet electrification, fleet retrofit, commercial fleet EV, vehicle electrification"
        path="/fleet"
      />
      <PageHero
        eyebrow="Fleet"
        breadcrumb="Fleet"
        title="Electrify Your Fleet Without Replacing Everything."
        description="Fleet electrification can be phased. A structured assessment identifies which vehicles suit retrofit, and planning aligns charging and deployment with your operations."
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Fleet Challenges"
              title="What Fleet Operators Face Today"
              description="Fleet decisions are operational decisions. They involve cost, uptime and the practicality of moving to electric."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {fleetChallenges.map((item, index) => (
              <Reveal key={item.title} delay={index * 0.06}>
                <article className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-64 w-full md:h-[440px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=fleet%20operations%20manager%20reviewing%20vehicle%20data%20on%20a%20tablet%20beside%20parked%20commercial%20electric%20vehicles%2C%20graphite%20and%20green%20tones%2C%20premium%20industrial%20photography%2C%20soft%20daylight&width=1400&height=900&seq=klz-fleetops-01&orientation=landscape"
                    alt="Fleet operations planning for vehicle electrification"
                    title="Fleet electrification planning and retrofit"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <SectionHeading
                eyebrow="Fleet Opportunity"
                title="A Phased Path to Electric Operations"
                description="Retrofit lets a fleet begin electrification with the vehicles that make the most sense, then expand. It reduces the need to replace an entire fleet before any benefit is realised."
              />
              <ul className="mt-8 space-y-3">
                {[
                  "Start with a manageable group of vehicles",
                  "Align retrofit work with operating schedules",
                  "Plan charging alongside the vehicle transition",
                  "Build visibility over vehicles and charging",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-check-line mt-0.5 text-primary-600" />
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Fleet Programme"
              title="From Assessment to Maintenance"
              description="A fleet programme covers the full path — assessment, planning, charging, deployment, monitoring and maintenance."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {fleetSteps.map((step, index) => (
              <Reveal key={step.title} delay={index * 0.05}>
                <article className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                    <i className={`${step.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="fleet-enquiry" className="w-full scroll-mt-24 bg-foreground-950 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              align="center"
              tone="dark"
              eyebrow="Fleet Enquiry"
              title="Request Fleet Assessment"
              description="Tell us about your fleet, routes and operating region. The team will review your requirement and respond through the official channel."
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-10 max-w-4xl">
              <FleetEnquiryForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}